import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, User, Phone, Mail, Home, Edit, Loader2,
  Search, Calendar, Save, X, PoundSterling, Bed, MapPin, Ban
} from "lucide-react";
import { API } from "@/App";

export const BuyerDetailPage = () => {
  const { buyerId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buyer, setBuyer] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Book Viewing Modal
  const [showBookViewing, setShowBookViewing] = useState(false);
  const [viewingForm, setViewingForm] = useState({
    date: "",
    time: "",
    property_address: "",
    property_id: "",
    notes: ""
  });
  const [properties, setProperties] = useState([]);
  const [bookingViewing, setBookingViewing] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchBuyer();
  }, [buyerId, token]);

  const fetchBuyer = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/sales/applicants/${buyerId}`, { headers });
      setBuyer(res.data);
      setEditForm(res.data);
    } catch (error) {
      toast.error("Failed to load buyer details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/sales/properties`, { headers });
      setProperties(res.data.properties || res.data || []);
    } catch (error) {
      console.error("Failed to load properties:", error);
    }
  };

  const handleBookViewing = async () => {
    if (!viewingForm.date || !viewingForm.time) {
      toast.error("Please select date and time");
      return;
    }
    if (!viewingForm.property_address) {
      toast.error("Please enter or select a property");
      return;
    }
    
    setBookingViewing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Create diary appointment
      await axios.post(`${API}/diary/appointments`, {
        title: `Viewing - ${buyer?.name || "Buyer"}`,
        appointment_type: "viewing",
        date: viewingForm.date,
        time: viewingForm.time,
        location: viewingForm.property_address,
        property_address: viewingForm.property_address,
        property_id: viewingForm.property_id,
        contact_name: buyer?.name || buyer?.display_name,
        contact_email: buyer?.email || buyer?.primary_email,
        contact_phone: buyer?.phone || buyer?.primary_phone,
        applicant_id: buyerId,
        notes: viewingForm.notes,
        department: "sales"
      }, { headers });
      
      toast.success("Viewing booked successfully!");
      setShowBookViewing(false);
      setViewingForm({ date: "", time: "", property_address: "", property_id: "", notes: "" });
    } catch (error) {
      toast.error("Failed to book viewing");
    } finally {
      setBookingViewing(false);
    }
  };

  const openBookViewingModal = async () => {
    await fetchProperties();
    setShowBookViewing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/sales/applicants/${buyerId}`, editForm, { headers });
      toast.success("Buyer updated successfully");
      setBuyer(editForm);
      setEditMode(false);
    } catch (error) {
      toast.error("Failed to update buyer");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.patch(`${API}/sales/applicants/${buyerId}/status`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      setBuyer({...buyer, status: newStatus});
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!buyer) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Buyer not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/sales")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sales
            </Button>
            <div>
              <p className="text-sm text-slate-500">Home &gt; Sales &gt; Buyers &gt; {buyer.name || buyer.display_name}</p>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-purple-700">{buyer.name || buyer.display_name}</h1>
                {buyer.status === "blacklisted" && (
                  <Badge className="bg-red-100 text-red-700">BLACKLISTED</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editMode ? (
              <>
                <Select value={buyer.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="looking">Looking</SelectItem>
                    <SelectItem value="found_property">Found Property</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="blacklisted">Blacklisted</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setEditMode(true)} className="bg-purple-600 hover:bg-purple-700">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="col-span-2 space-y-6">
            {/* Basic Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Basic Details</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input value={editForm.name || ""} onChange={(e) => setEditForm({...editForm, name: e.target.value})} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({...editForm, email: e.target.value})} />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Mobile Phone</Label>
                        <Input value={editForm.mobile_phone || ""} onChange={(e) => setEditForm({...editForm, mobile_phone: e.target.value})} />
                      </div>
                      <div>
                        <Label>Home Phone</Label>
                        <Input value={editForm.home_phone || ""} onChange={(e) => setEditForm({...editForm, home_phone: e.target.value})} />
                      </div>
                      <div>
                        <Label>Work Phone</Label>
                        <Input value={editForm.work_phone || ""} onChange={(e) => setEditForm({...editForm, work_phone: e.target.value})} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{buyer.name || buyer.display_name}</span>
                    </div>
                    {(buyer.email || buyer.primary_email) && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <a href={`mailto:${buyer.email || buyer.primary_email}`} className="text-purple-600 hover:underline">
                          {buyer.email || buyer.primary_email}
                        </a>
                      </div>
                    )}
                    {(buyer.phone || buyer.mobile_phone || buyer.primary_phone) && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>{buyer.mobile_phone || buyer.phone || buyer.primary_phone}</span>
                      </div>
                    )}
                    {buyer.home_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span>Home: {buyer.home_phone}</span>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Search Criteria */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Search className="w-5 h-5" />
                  Search Criteria
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Min Price (£)</Label>
                        <Input 
                          type="number" 
                          value={editForm.budget_min || ""} 
                          onChange={(e) => setEditForm({...editForm, budget_min: parseInt(e.target.value) || null})} 
                        />
                      </div>
                      <div>
                        <Label>Max Price (£)</Label>
                        <Input 
                          type="number" 
                          value={editForm.budget_max || ""} 
                          onChange={(e) => setEditForm({...editForm, budget_max: parseInt(e.target.value) || null})} 
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Min Bedrooms</Label>
                        <Input 
                          type="number" 
                          value={editForm.bedrooms_min || ""} 
                          onChange={(e) => setEditForm({...editForm, bedrooms_min: parseInt(e.target.value) || null})} 
                        />
                      </div>
                      <div>
                        <Label>Max Bedrooms</Label>
                        <Input 
                          type="number" 
                          value={editForm.bedrooms_max || ""} 
                          onChange={(e) => setEditForm({...editForm, bedrooms_max: parseInt(e.target.value) || null})} 
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Property Types</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {["Detached", "Semi-detached", "Terraced", "Flat", "Bungalow", "Land"].map(type => (
                          <label key={type} className="flex items-center gap-2 cursor-pointer">
                            <input 
                              type="checkbox"
                              checked={(editForm.property_types || []).includes(type)}
                              onChange={(e) => {
                                const types = editForm.property_types || [];
                                if (e.target.checked) {
                                  setEditForm({...editForm, property_types: [...types, type]});
                                } else {
                                  setEditForm({...editForm, property_types: types.filter(t => t !== type)});
                                }
                              }}
                              className="rounded border-slate-300"
                            />
                            <span className="text-sm">{type}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Preferred Areas</Label>
                      <Input 
                        value={editForm.preferred_areas || ""} 
                        onChange={(e) => setEditForm({...editForm, preferred_areas: e.target.value})} 
                        placeholder="e.g. Bedford, Kempston, Biddenham"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <PoundSterling className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-500">Price Range:</span>
                        <span className="font-medium">
                          {buyer.budget_min ? `£${buyer.budget_min.toLocaleString()}` : 'No min'} - {buyer.budget_max ? `£${buyer.budget_max.toLocaleString()}` : 'No max'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Bed className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-500">Bedrooms:</span>
                        <span className="font-medium">
                          {buyer.bedrooms_min || 'Any'} - {buyer.bedrooms_max || 'Any'}
                        </span>
                      </div>
                    </div>
                    {buyer.property_types && buyer.property_types.length > 0 && (
                      <div>
                        <span className="text-sm text-slate-500">Property Types:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {buyer.property_types.map(type => (
                            <Badge key={type} variant="outline">{type}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {buyer.preferred_areas && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                        <div>
                          <span className="text-sm text-slate-500">Preferred Areas:</span>
                          <p className="font-medium">{buyer.preferred_areas}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <Textarea 
                    value={editForm.notes || ""} 
                    onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                    rows={4}
                    placeholder="Add notes about this buyer..."
                  />
                ) : (
                  <p className="text-slate-600">{buyer.notes || "No notes"}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Info */}
          <div className="space-y-6">
            {/* Status Card */}
            <Card>
              <CardContent className="p-4">
                <div className="text-center">
                  <Badge className={`text-lg px-4 py-2 ${
                    buyer.status === "looking" ? "bg-green-100 text-green-700" :
                    buyer.status === "found_property" ? "bg-blue-100 text-blue-700" :
                    buyer.status === "blacklisted" ? "bg-red-100 text-red-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    {buyer.status === "looking" ? "Looking" :
                     buyer.status === "found_property" ? "Found Property" :
                     buyer.status === "blacklisted" ? "Blacklisted" :
                     buyer.status || "Active"}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Date Registered</span>
                    <span className="font-medium">{buyer.created_at ? new Date(buyer.created_at).toLocaleDateString('en-GB') : '-'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Branch</span>
                    <span className="font-medium">{buyer.branch || "Kempston Office"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Source</span>
                    <span className="font-medium">{buyer.source || "-"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  <Search className="w-4 h-4 mr-2" />
                  Find Matching Properties
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={openBookViewingModal}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Viewing
                </Button>
                {buyer.status !== "blacklisted" && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-red-600 hover:text-red-700"
                    onClick={() => handleStatusChange("blacklisted")}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Blacklist Buyer
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Book Viewing Modal */}
      <Dialog open={showBookViewing} onOpenChange={setShowBookViewing}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-purple-700">Book Viewing</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm text-purple-700">
                Booking viewing for: <strong>{buyer?.name || buyer?.display_name}</strong>
              </p>
            </div>

            {/* Property Selection */}
            <div>
              <Label>Property *</Label>
              <Select 
                value={viewingForm.property_id} 
                onValueChange={(v) => {
                  const prop = properties.find(p => p.property_id === v || p.case_id === v);
                  setViewingForm({
                    ...viewingForm, 
                    property_id: v, 
                    property_address: prop?.address || prop?.property_address || ""
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a property..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop, idx) => (
                    <SelectItem key={idx} value={prop.property_id || prop.case_id}>
                      {prop.address || prop.property_address} - £{(prop.price || 0).toLocaleString()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Or enter address manually below</p>
            </div>

            <div>
              <Label>Property Address</Label>
              <Input 
                value={viewingForm.property_address}
                onChange={(e) => setViewingForm({...viewingForm, property_address: e.target.value})}
                placeholder="Enter property address"
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date"
                  value={viewingForm.date}
                  onChange={(e) => setViewingForm({...viewingForm, date: e.target.value})}
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input 
                  type="time"
                  value={viewingForm.time}
                  onChange={(e) => setViewingForm({...viewingForm, time: e.target.value})}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notes</Label>
              <Textarea 
                value={viewingForm.notes}
                onChange={(e) => setViewingForm({...viewingForm, notes: e.target.value})}
                placeholder="Any notes for this viewing..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookViewing(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBookViewing} 
              disabled={bookingViewing}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {bookingViewing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Booking...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Viewing
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BuyerDetailPage;
